import {
    atom,
    useRecoilValue,
    useSetRecoilState,
} from "recoil";
import { urlSyncEffect } from "recoil-sync";
import { array, number } from "@recoiljs/refine";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { axios } from "../lib/request";
import { npcsUri, npcsQueryKey } from "../lib/refresh";
import { useItemQuery } from "../Global.state";
import { INFINITE_QUERY_KEY, MAX_PER_PAGE } from "../consts/request";
import { useEffect, useMemo } from "react";
import { OPEN_STATES_KEYS } from "../consts/states";
import { getErrorMessage } from "../lib/error";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";

export const deleteIdState = atom({
    key: "npc-items/deleteId",
    default: 0,
});

export const pageState = atom({
    key: "npc-items/page",
    default: 1,
    effects: [
        urlSyncEffect({
            refine: number(),
            history: 'push'
        })
    ],
});

export const pageSizeState = atom({
    key: "npc-items/pageSize",
    default: MAX_PER_PAGE,
    effects: [
        urlSyncEffect({
            refine: number(),
            history: 'push'
        })
    ],
});

export const highlightIdState = atom({
    key: "npc-items/highlightId",
    default: 0,
})

export const termState = atom({
    key: "npc-items/term",
    default: "",
});

export const openTabState = atom({
    key: OPEN_STATES_KEYS.NPC,
    default: [],
    effects: [
        urlSyncEffect({
            refine: array(number()),
            history: 'push'
        })
    ],
});

/**
 * Fetches paginated NPC (Non-Player Character) data from the API.
 * Transforms the raw API response to normalize NPC media into the standard `assets.photos` structure.
 * 
 * @param {string} game_id - The ID of the game.
 * @param {string} term - The search term.
 * @param {number} page - The current page number.
 * @param {number} per_page - The number of items per page.
 * @returns {Promise<Object>} The NPCs payload including metadata.
 */
export const fetchNpcs = async (game_id, term, page, per_page) => {
    if (game_id === "new") {
        return { npcs: [] };
    }

    const params = { term, page, per_page };
    const { data } = await axios.get(npcsUri(game_id), { params });
    data.npcs = (data.npcs || []).map(npc => {
        if (npc?.media_url) {
            // Transform NPC media into standard structure
            npc.assets = {
                photos: [{
                    name: 'media',
                    url: npc.media_url,
                }]
            }
        }
        return npc
    })
    return data
};

export const updateNpc = async (game_id, id, params) => {
    const url = `${process.env.REACT_APP_API_NPCS_UPDATE_URL}/${id}.json`;
    return await axios.put(url, { ...params, game_id });
};

export const useQueryKey = (game_id) => {
    const page = useRecoilValue(pageState);
    const pageSize = useRecoilValue(pageSizeState);
    const term = useRecoilValue(termState);
    return npcsQueryKey(game_id, term, page, pageSize);
};

export const useInfiniteQueryKey = (game_id) => {
    return npcsQueryKey(game_id, INFINITE_QUERY_KEY, 1, MAX_PER_PAGE)
}

/**
 * A React Query hook that fetches paginated NPCs based on the current Recoil state.
 * Uses `suspense: true` for loading states and handles global error alerts.
 * 
 * @param {string} game_id - The ID of the game.
 * @returns {import("react-query").UseQueryResult} The query result object.
 */
export const useNpcsDataQuery = (game_id) => {
    const term = useRecoilValue(termState);
    const page = useRecoilValue(pageState);
    const pageSize = useRecoilValue(pageSizeState);
    const queryKey = useQueryKey(game_id);
    const setAlert = useSetRecoilState(alertState);
    return useQuery(
        queryKey,
        () => fetchNpcs(game_id, term, page, pageSize),
        { 
            keepPreviousData: true, 
            suspense: true,
            useErrorBoundary: false,
            onError: (error) => {
                const message = getErrorMessage(error);
                setAlert({ type: "error", message, closable: true });
            }
        }
    );
};

/**
 * A React Query hook that manages infinite scrolling/pagination for NPCs.
 * Used when displaying NPCs in a continuous list rather than a paginated table.
 * 
 * @returns {Object} An object containing the flattened NPCs array, pagination controls, and loading states.
 */
export const useAllNpcsQuery = () => {
    const { game_id } = useParams();

    const {
        data,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetched,
    } = useInfiniteQuery(
        useInfiniteQueryKey(game_id),
        ({ pageParam }) => fetchNpcs(game_id, "", pageParam, MAX_PER_PAGE),
        {
            getNextPageParam: (lastPage, pages) => {
                const { count } = lastPage?.meta || {};
                const loaded = pages.reduce(
                    (sum, page) => (sum += page?.npcs.length),
                    0
                );

                if ((lastPage && (lastPage?.npcs || []).length === 0) || count <= loaded) {
                    return undefined;
                }

                return Number(lastPage?.meta?.page || 0) + 1;
            },
        }
    );

    const npcs = (data?.pages || []).reduce((result, page) => {
        return [
            ...result,
            ...(page?.npcs || []),
        ]
    }, [])

    const nextPage = [...data?.pageParams || []].pop()

    return {
        nextPage,
        npcs,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetched,
    };
};

export const useInfiniteNpcs = () => {
    const {
        nextPage,
        npcs,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetched,
    } = useAllNpcsQuery()

    useEffect(() => {
        let loaded = false
        if (!loaded && hasNextPage) {
            fetchNextPage()
        }
        return () => {
            loaded = true
        }
    }, [
        nextPage,
        hasNextPage,
        fetchNextPage,
    ])
    
    return {
        isLoading: isFetchingNextPage,
        isFetched,
        npcs,
    }
}

export const useNpcsQuery = (game_id) => {
    const { data } = useNpcsDataQuery(game_id);
    return data?.npcs;
};

export const useNpcItemsCountQuery = (game_id) => {
    const { data } = useNpcsDataQuery(game_id);
    return data?.meta?.count;
};

export const useNpcItemsFetchingQuery = (game_id) => {
    const { isFetching } = useNpcsDataQuery(game_id);
    return isFetching;
};

export const useNpcItemsPagesQuery = (game_id) => {
    const { data } = useNpcsDataQuery(game_id);
    return data?.meta?.pages;
};

const getNpcActionContext = ({ id, item }) => {
    return item?.name ? `saving NPC "${item.name}" (ID ${id})` : `saving NPC ID ${id}`;
};

export const useNpcItemsQuery = (id, config) => {
    const { game_id } = useParams();
    const { data: sourceData, isFetching } = useNpcsDataQuery(game_id);
    const queryKey = useQueryKey(game_id);
    const setter = (params) => updateNpc(game_id, id, params);
    const data = useMemo(() => {
        const { npcs = [] } = {...sourceData} || {};
        sourceData.npcs = npcs.map(d => {
            const { locks, timer, unlock_by_location_radius, unlock_by_task_ids, timers } = d
            d.unlock_by_task_ids = unlock_by_task_ids || (locks?.tasks || []).map(t => {
                const { id, answers = [] } = t
                return `${id}${answers.length > 0 ? `;${answers.join(':')}`: ''}`
            }).join(',')
            d.unlock_by_location_radius = unlock_by_location_radius || locks?.location?.radius
            d.timers = timers || timer
            return d
        })
        return sourceData
    }, [sourceData])
    const actionContextConfig = {
        actionContext: getNpcActionContext,
        ...config,
    };
    return [...useItemQuery(data, id, "npcs", queryKey, setter, actionContextConfig), isFetching];
};

export const useQueryNoInvalidate = (id) => useNpcItemsQuery(id, { invalidate: false });
export const useQueryInvalidateInfinite = (id) => {
    const { game_id } = useParams();
    const page = useRecoilValue(pageState);
    return useNpcItemsQuery(id, {
        invalidateKey: useInfiniteQueryKey(game_id),
        refetchType: 'none',
        refetchPage: data => {
            return String(data?.meta?.page) === String(page)
        },
    })
}

export const useCreateMutation = () => {
    const { game_id } = useParams();
    const queryClient = useQueryClient();
    const pageSize = useRecoilValue(pageSizeState);
    const setPage = useSetRecoilState(pageState);
    const setTerm = useSetRecoilState(termState);
    const queryKey = useQueryKey(game_id);

    const { isLoading, mutateAsync } = useMutation(
        () =>
            axios.post(process.env.REACT_APP_API_NPCS_URL, {
                game_id,
            }),
        {
            onSuccess: (data) => {
                const current = queryClient.getQueryData(queryKey);
                const { non_player_character } = data?.data || {};
                const newNpcs = [non_player_character, ...current?.npcs]
                const newPages = Math.ceil(
                    newNpcs.length / pageSize
                )
                queryClient.setQueryData(queryKey, {
                    ...current,
                    npcs: newNpcs,
                    meta: {
                        ...current?.meta || {},
                        pages: newPages,
                    },
                });
                setPage(1);
                setTerm("");
                queryClient.invalidateQueries(queryKey)
            },
        }
    );

    return {
        isCreating: isLoading,
        create: mutateAsync,
    };
};

export const useDestroyMutation = () => {
    const { game_id } = useParams();
    const pageSize = useRecoilValue(pageSizeState);
    const queryClient = useQueryClient();
    const queryKey = useQueryKey(game_id);

    const { isLoading, mutate } = useMutation(
        (deleteId) =>
            axios.delete(
                `${process.env.REACT_APP_API_NPCS_DELETE_URL}/${deleteId}.json?game_id=${game_id}`
            ),
        {
            onMutate: (deleteId) => {
                const data = queryClient.getQueryData(queryKey);
                const npcs = data?.npcs.filter((npc) => npc.id !== deleteId);
                const existingPages = (data?.meta?.pages || 1)
                const newPages = Math.ceil(
                    npcs.length / pageSize
                )
                queryClient.setQueryData(queryKey, {
                    ...data,
                    npcs,
                    meta: {
                        ...data?.meta || {},
                        pages: Math.max(
                            newPages,
                            existingPages - (npcs.length > 0 ? 0 : 1)
                        ),
                    },
                });
                queryClient.cancelQueries(queryKey);
            },
        }
    );

    return {
        isDeleting: isLoading,
        destroy: mutate,
    };
};

export const useInvalidateInfinity = () => {
    const { game_id } = useParams();
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries(npcsQueryKey(game_id, INFINITE_QUERY_KEY, 1, MAX_PER_PAGE))
}
